import React, { useCallback, useMemo, useState } from 'react'
import { cloneDeep } from 'lodash'
import { useCancelToken } from '../../utils/hooks/useCancelToken'
import { makeCall, setStateByKey } from '../../utils/api/makeCall'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { emptyGuid } from '../../utils/emptyGuid'
import { setPointerErrorsFromApiResponse } from "../../utils/api/apiUtils"
import { newGuid } from '../../utils/objectutils'
import { waitForPromise } from '../../utils/utils'

const articleTemplateObj = {
  baseData: {
    internalName: {
      values: []
    },
    externalName: {
      values: []
    },
    description: {
      values: []
    },
    comment: {
      values: []
    },
    foreignKey: "",
    number: "",
    class: {
      internalName: {
        values: []
      },
      externalName: {
        values: []
      },
      foreignKey: "",
      isWithSizes: false,
      id: emptyGuid
    },
    supplier: {
      id: emptyGuid
    },
    storehouse: {
      id: emptyGuid
    },
    unit: {
      id: emptyGuid
    },
    followerArticle: {
      displayName: "",
      articleNumber: "",
      id: emptyGuid
    },
    isLocked: false,
    isObsolet: false,
    isAvailableMale: false,
    isAvailableFeMale: false,
    contingentsCostperItem: 0,
    packageSize:0,
    supplierAlias: "",
    id: emptyGuid
  },
  sizeData: [],
  assignedOffices: []
}

const articleBaseObjTemplate = {
  internalName: {
    values: []
  },
  externalName: {
    values: []
  },
  description: {
    values: []
  },
  comment: {
    values: []
  },
  foreignKey: "",
  number: "",
  class: {
    internalName: {
      values: []
    },
    externalName: {
      values: []
    },
    unit: {
      id: emptyGuid
    },
    foreignKey: "",
    isWithSizes: false,
    id: emptyGuid
  },
  supplier: {
    id: emptyGuid
  },
  storehouse: {
    id: emptyGuid
  },
  unit: {
    id: emptyGuid
  },
  followerArticle: {
    displayName: "",
    articleNumber: "",
    id: emptyGuid
  },
  price: {
    value: 0,
    currency: {
      isoCode: "",
      id: emptyGuid
    },
    id: emptyGuid
  },
  isLocked: false,
  isObsolet: false,
  isAvailableMale: false,
  isAvailableFeMale: false,
  contingentsCostperItem: 0,
  packageSize:0,
  supplierAlias: "",
  id: emptyGuid
}

const sizeSystemTemplateObj = {
  internalName: {
    values: []
  },
  externalName: {
    values: []
  },
  sizes: [],
  foreignKey: "",
  id: emptyGuid
}
export const sizeTemplateObj = {
  name: {
    values: []
  },
  dimension1: {
    values: []
  },
  dimension2: {
    values: []
  },
  price: {
    value: 0,
    currency: {
      isoCode: "",
      id: emptyGuid
    },
    id: emptyGuid
  },
  isLocked: false,
  isObsolet: false,
  foreignKey: "",
  supplierAlias: "",
  id: emptyGuid
}
const ArticleDataContext = React.createContext({})
export const useArticleDataState = () => {
  return React.useContext(ArticleDataContext)
}

const ArticleDataProvider = ({ children }) => {
  const [successSubmit, setSuccessSubmit] = useState(false)
  const [errorStates, setErrorStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [successStates, setSuccessStates] = useState({})
  const [article, setArticle] = useState(articleTemplateObj)
  const [redirectId, setRedirectId] = useState('')
  const [articleBaseObj, setArticleBaseObj] = useState(articleBaseObjTemplate)
  const [initialArticleBaseObj, setInitialArticleBaseObj] = useState(JSON.stringify(articleBaseObjTemplate))
  const [selectedOfficeAvailabilities, setSelectedOfficeAvailabilities] = useState([])
  const [initialSelectedOfficeAvailabilities, setInitialSelectedOfficeAvailabilities] = useState(JSON.stringify([]))
  const [sizeSystems, setSizeSystems] = useState([])
  const [documentContainers, setDocumentContainers] = useState([])

  const [initialSizeSystems, setInitialSizeSystems] = useState(JSON.stringify([]))

  const [isLoading, setIsLoading] = useState(false)
  const [suppliers, setSuppliers] = useState([])
  const [storehouses, setStorehouses] = useState([])
  const [units, setUnits] = useState([])
  const [articleDetails, setArticleDetails] = useState(null)

  const [loadedArticleData, setLoadedArticleData] = useState(null)
  const { createOrCancelToken, sourceRef, isCancel } = useCancelToken();
  const [formErrors, setFormErrors] = useState({})
  const [errorsOccurredOnSubmit, setErrorsOccurredOnSubmit] = useState(false)
  const [actionMode, setActionMode] = useState('edit')
  const [bigData, setBigData] = useState(null)
  const loadArticleData = useCallback(async id => {
    var calls = ["article", "officeAvailabilities", "sizeSystems"]
    var promises = []
    var loadedData = {}

    for (let call of calls) {
      promises.push(apiClient.getJson(APIEndpoints.articleAdmin(id)[call]))
    }
    Promise.all(promises).then((responses) => {
      calls.forEach((call, index) => loadedData[call] = responses[index])
      setLoadedArticleData(loadedData);
      initLoadedArticleData(cloneDeep(loadedData));
    })
  }, [])
  const initLoadedArticleData = (loadedData) => {
    const loadedArticle = loadedData.article
    const loadedOfficeAvailabilities = loadedData.officeAvailabilities
    const loadedSizeSystems = loadedData.sizeSystems

    // init baseData fields
    const newBaseData = {
      ...loadedArticle,
      number: "",
      foreignKey: "",
      supplierAlias: "",
      price: loadedArticle.price ? {
        ...loadedArticle.price,
        value: null
      } : null,
      internalName: {
        values: []
      },
      externalName: {
        values: []
      },
      supplier: { id: loadedArticle.supplier ? loadedArticle.supplier.id : emptyGuid },
      storehouse: { id: loadedArticle.storehouse ? loadedArticle.storehouse.id : emptyGuid },
      unit: { id: loadedArticle.unit ? loadedArticle.unit.id : emptyGuid },
    };
    setArticleBaseObj(newBaseData)
    setInitialArticleBaseObj(JSON.stringify(newBaseData));

    setSelectedOfficeAvailabilities(loadedOfficeAvailabilities)
    setInitialSelectedOfficeAvailabilities(JSON.stringify(loadedOfficeAvailabilities))

    const newSizeData = cloneDeep(loadedSizeSystems)
    newSizeData.forEach(item => {
      item.sizes.forEach(size => {
        if (size.price) {
          size.price.value = null
        }
        size.supplierAlias = ""
      })
    })
    setSizeSystems(newSizeData)
    setInitialSizeSystems(JSON.stringify(newSizeData))
  }

  async function getArticleBaseData(articleId) {
    await makeCall('getArticleBaseData', async () => {
      let loadedArticle = await apiClient.getJson(APIEndpoints.articleAdmin(articleId).article)
      setArticleBaseObj(loadedArticle)
    }, setErrorStates, setLoadingStates)
  }
  const selectedOfficeAvailabilitiesHasChanged = useMemo(() => {
    if (initialSelectedOfficeAvailabilities !== '') {
      const selectedOfficeAvailabilitiesString = JSON.stringify(selectedOfficeAvailabilities)
      return selectedOfficeAvailabilitiesString !== initialSelectedOfficeAvailabilities
    }
  }, [selectedOfficeAvailabilities, initialSelectedOfficeAvailabilities])

  const baseDataHasChanged = useMemo(() => {
    if (initialArticleBaseObj !== '') {
      const baseDataString = JSON.stringify(articleBaseObj)
      return baseDataString !== initialArticleBaseObj
    }
  }, [articleBaseObj, initialArticleBaseObj])

  const sizeSystemsHasChanged = useMemo(() => {
    if (initialSizeSystems !== '') {
      const sizeSystemsString = JSON.stringify(sizeSystems)
      return sizeSystemsString !== initialSizeSystems
    }
  }, [sizeSystems, initialSizeSystems])

  const formDataHasChanged = useMemo(() => {
    return selectedOfficeAvailabilitiesHasChanged || baseDataHasChanged || sizeSystemsHasChanged
  }, [selectedOfficeAvailabilitiesHasChanged, baseDataHasChanged, sizeSystemsHasChanged])

  function patchArticleBaseData(articleId) {
    makeCall('saveArticle', async () => {
      await apiClient.patchJson(APIEndpoints.articleAdmin(articleId).article, articleBaseObj)
      await getArticleBaseData(articleId)
      setStateByKey(setSuccessStates, 'saveArticle', true)
    }, setErrorStates, setLoadingStates)
  }
  const getSizeSystemTemplates = useCallback(async (force) => {
    if ((bigData && Object.keys(bigData).length) && !force) {
      return bigData
    }
    else if (loadingStates.sizeSystemTemplates) {
      await waitForPromise(100)
      return await getSizeSystemTemplates()
    }
    else {
      let articleId = articleBaseObj.id
      let newBigData = {}
      await makeCall('sizeSystemTemplates', async () => {
        let loadedSizeSystems = await apiClient.getJson(APIEndpoints.articleAdmin(articleId).sizeSystems)
        const loadedSystems = cloneDeep(loadedSizeSystems)
        for (let system of loadedSystems) {
          let newValue = system
          //newValue.sizes = {}
          newBigData[system.id] = newValue
          let newSizes = {}
          let loadedSizes = cloneDeep(system.sizes)
          for (let size of loadedSizes) {
            let newSizeValue = size
            newSizes[size.id] = newSizeValue
          }
          newBigData[system.id].sizes = newSizes
          newBigData[system.id].sizesLoaded = true
          if (loadedSystems.length === 1) {
            newBigData[system.id].open = true
          }
        }
        setBigData(newBigData)
      }, setErrorStates, setLoadingStates)
      return newBigData
    }
  }, [bigData, loadingStates.sizeSystemTemplates, articleBaseObj.id])

  const getSizes = useCallback(async (sizeSystemId, force) => {
    let loadedBigData = await getSizeSystemTemplates()
    if (loadedBigData[sizeSystemId].sizesLoaded && !force) {
      return null
    }
    let articleId = articleBaseObj.id
    await makeCall('sizes', async () => {
      let loadedSizes = await apiClient.getJson(APIEndpoints.articleAdmin(articleId, sizeSystemId).sizes)
      let newSizes = {}
      for (let size of loadedSizes) {
        let newValue = size
        newSizes[size.id] = newValue
      }
      setBigData(prev => {
        let newData = cloneDeep(prev)
        newData[sizeSystemId].sizes = newSizes
        newData[sizeSystemId].sizesLoaded = true
        return newData
      })

    }, setErrorStates, setLoadingStates)
  }, [getSizeSystemTemplates, articleBaseObj.id])

  async function deleteSize(sizeSystemId, sizeId) {
    createOrCancelToken()
    let articleId = articleBaseObj.id
    await makeCall('deleteSize', async () => {
      await apiClient.deleteJson(APIEndpoints.articleAdmin(articleId, sizeSystemId, sizeId).size)
      await getSizes(sizeSystemId, true)
      setStateByKey(setSuccessStates, 'deleteSize', true)
    }, setErrorStates, setLoadingStates, isCancel, articleBaseObj.id)
  }
  async function postSize(sizeSystemId, size, handleClose) {
    createOrCancelToken()
    let articleId = articleBaseObj.id
    //redirect will be after the call is successful
    await makeCall('saveSize', async () => {
      await apiClient.postJson(APIEndpoints.articleAdmin(articleId, sizeSystemId).createSize, size)
      getSizes(sizeSystemId, true)
      setStateByKey(setSuccessStates, 'saveSize', true)
      handleClose()
    }, setErrorStates, setLoadingStates, isCancel, articleBaseObj.id)
  }

  async function patchSize(sizeSystemId, sizeId, size, handleClose) {
    createOrCancelToken()
    let articleId = articleBaseObj.id
    await makeCall('saveSize', async () => {
      await apiClient.patchJson(APIEndpoints.articleAdmin(articleId, sizeSystemId, sizeId).size, size)
      getSizes(sizeSystemId, true)
      setStateByKey(setSuccessStates, 'saveSize', true)
      handleClose()
    }, setErrorStates, setLoadingStates, isCancel, articleBaseObj.id)
  }
  async function putSizeSystemsOrderSequence(newSequence) {
    createOrCancelToken()
    const articleId = articleBaseObj.id
    makeCall('saveOrderSequence', async () => {
      await apiClient.putJson(APIEndpoints.articleAdmin(articleId).sizeSystemsOrderSequences, newSequence, true, false, { cancelToken: sourceRef.current.token })
      await getSizeSystems(articleId)
    }, setErrorStates, setLoadingStates, isCancel, articleBaseObj.id)
  }

  async function putSizesOrderSequence(sizeSystemId, newSequence) {
    createOrCancelToken();
    const articleId = articleBaseObj.id
    makeCall('saveOrderSequence', async () => {
      await apiClient.putJson(APIEndpoints.articleAdmin(articleId, sizeSystemId).sizesOrderSequences, newSequence, true, false, { cancelToken: sourceRef.current.token })
      getSizes(sizeSystemId, true)
    }, setErrorStates, setLoadingStates, isCancel, articleBaseObj.id)
  }



  const getSizeSystems = useCallback((articleId) => {
    makeCall('getSizeSystems', async () => {
      let loadedSizeSystems = await apiClient.getJson(APIEndpoints.articleAdmin(articleId).sizeSystems)
      setInitialSizeSystems(JSON.stringify(loadedSizeSystems))
      setSizeSystems(loadedSizeSystems)
    }, setErrorStates, setLoadingStates)
  }, [])
  async function saveSizeSystems(articleId) {
    await makeCall('saveSizeSystems', async () => {
      let sizeData = getPreparedSizeData()
      await apiClient.postJson(APIEndpoints.articleAdmin(articleId).sizeSystems, sizeData)
      await getSizeSystems(articleId)
      setIsLoading(false)
      setStateByKey(setSuccessStates, 'saveSizeSystems', true)
    }, setErrorStates, setLoadingStates)
  }

  const getArticleOfficeAvailabilities = useCallback((articleId) => {
    makeCall('getArticleOfficeAvailabilities', async () => {
      let loadedOfficeAvailabilities = await apiClient.getJson(APIEndpoints.articleAdmin(articleId).officeAvailabilities)
      setInitialSelectedOfficeAvailabilities(JSON.stringify(loadedOfficeAvailabilities))
      setSelectedOfficeAvailabilities(loadedOfficeAvailabilities)
    }, setErrorStates, setLoadingStates)
  }, [])
  async function saveArticleOfficeAvailabilities(articleId, officeIds) {
    await makeCall('saveArticleOfficeAvailabilities', async () => {
      await apiClient.postJson(APIEndpoints.articleAdmin(articleId).officeAvailabilities, officeIds)
      setIsLoading(false)
      setStateByKey(setSuccessStates, 'saveArticleOfficeAvailabilities', true)
    }, setErrorStates, setLoadingStates)
  }
  const getSuppliers = useCallback(() => {
    makeCall('suppliers', async () => {
      let loadSuppliers = await apiClient.getJson(APIEndpoints.suppliers)
      setSuppliers(loadSuppliers)
    }, setErrorStates, setLoadingStates)
  }, [])
  const getStorehouses = useCallback(async () => {
    makeCall('storehouses', async () => {
      let loadedStorehouses = await apiClient.getJson(APIEndpoints.storehouses())
      setStorehouses(loadedStorehouses)
    }, setErrorStates, setLoadingStates)

  }, [])
  const getSupplierAlias = useCallback((articleId, supplierId) => {
    makeCall('supplierAlias', async () => {
      let loadSupplierAlias = await apiClient.getJson(APIEndpoints.articleAdminSupplierAlias(articleId, supplierId))
      setArticleBaseObj((prev) => ({
        ...prev,
        'supplierAlias': loadSupplierAlias,
      }))
    }, setErrorStates, setLoadingStates)
  }, [])
  const getUnits = useCallback(() => {
    makeCall('units', async () => {
      let loadUnits = await apiClient.getJson(APIEndpoints.units)
      setUnits(loadUnits)
    }, setErrorStates, setLoadingStates)
  }, [])
  const getArticleDetails = useCallback(async id => {
    makeCall('articleDetails', async () => {
      let loadedArticleDetails = await apiClient.getJson(APIEndpoints.article(id))
      setArticleDetails(loadedArticleDetails)
    }, setErrorStates, setLoadingStates)
  }, [])
  const getDocumentContainers = useCallback(async id => {
    makeCall('documentContainers', async () => {
      let loadedDocumentContainers = await apiClient.getJson(APIEndpoints.articleAdmin(id).documents)
      setDocumentContainers(loadedDocumentContainers)
    }, setErrorStates, setLoadingStates)
  }, [])
  const resetFormData = useCallback(() => {
    setArticleBaseObj(JSON.parse(initialArticleBaseObj))
    setSelectedOfficeAvailabilities(JSON.parse(initialSelectedOfficeAvailabilities))
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
  }, [setFormErrors, setErrorsOccurredOnSubmit, initialArticleBaseObj, initialSelectedOfficeAvailabilities])

  const getPreparedBaseData = useCallback(() => {
    const preparedBaseData = cloneDeep(articleBaseObj);
    delete preparedBaseData.metaProperties
    return preparedBaseData
  }, [articleBaseObj])
  const getPreparedSizeData = useCallback(() => {
    const preparedSizeData = cloneDeep(sizeSystems)
    preparedSizeData.forEach(item => {
      // change all id's to emptyGuids for the new positions (we have to send out empty guids when saving)
      if (item.isNewPosition) {
        item.id = emptyGuid
        delete item.isNewPosition
      }
      item.sizes.forEach(size => {
        if (size.isNewPosition) {
          size.id = emptyGuid
          delete size.isNewPosition
        }
        delete size.metaProperties
      })
      delete item.metaProperties
    })
    return preparedSizeData
  }, [sizeSystems])
  const submitCopyArticle = useCallback(async () => {
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
    let articleId = articleBaseObj.id
    let sizeData = getPreparedSizeData()
    if (sizeData.length > 1) {
      sizeData = sizeData.filter(system => system.isSelected)
    }
    let baseData = getPreparedBaseData()
    var preparedData = { baseData: baseData, assignedOffices: selectedOfficeAvailabilities, sizeData: sizeData }
    try {
      const createdArticleId = await apiClient.postJson(APIEndpoints.articleAdmin(articleId).copyArticle, preparedData)
      setSuccessSubmit(true)
      return { articleId: createdArticleId, articleNumber: articleBaseObj.number }
    } catch (e) {
      setErrorsOccurredOnSubmit(true)
      setFormErrors(setPointerErrorsFromApiResponse(e))
    }
    return null
  }, [articleBaseObj, selectedOfficeAvailabilities, getPreparedBaseData, getPreparedSizeData])

  async function uploadDocument(containerId, description, files, uploadURL) {
    let articleId = articleBaseObj.id
    let params = { containerId: containerId, objectId: articleId, description: description, uploadURL: uploadURL }
    //redirect will be after the call is successful
    await makeCall('uploadDocument', async () => {
      await apiClient.postFile(APIEndpoints.uploadDocumentsAdmin, { data: JSON.stringify(params) }, files)
      getDocumentContainers(articleId)
      setStateByKey(setSuccessStates, 'uploadDocument', true)
    }, setErrorStates, setLoadingStates)
  }
  async function removeDocument(documentId) {
    //redirect will be after the call is successful
    await makeCall('removeDocument', async () => {
      let articleId = articleBaseObj.id
      await apiClient.deleteJson(APIEndpoints.documentAdmin(documentId).documents)
      getDocumentContainers(articleId)
      setStateByKey(setSuccessStates, 'removeDocument', true)
    }, setErrorStates, setLoadingStates)
  }
  async function updateDocumentDescription(documentId, description) {
    //redirect will be after the call is successful
    await makeCall('updateDocumentDescription', async () => {
      let articleId = articleBaseObj.id
      await apiClient.patchJson(APIEndpoints.documentAdmin(documentId).documents, description)
      getDocumentContainers(articleId)
      setStateByKey(setSuccessStates, 'updateDocumentDescription', true)
    }, setErrorStates, setLoadingStates)
  }

  const addNewSizeToSystem = () => {
    if ((sizeSystems?.length ?? 0) > 1) return //for mor then one sizesystems not implemented now!!!
    let index = 0
    let newSizeSystems = [...sizeSystems]
    if (!sizeSystems?.length) {
      const clonedSizeSystemTemplate = cloneDeep(sizeSystemTemplateObj)
      clonedSizeSystemTemplate.id = newGuid()
      clonedSizeSystemTemplate.isNewPosition = true
      setSizeSystems(clonedSizeSystemTemplate)
      newSizeSystems = [clonedSizeSystemTemplate]
    }
    const clonedSizeTemplate = cloneDeep(sizeTemplateObj)
    clonedSizeTemplate.id = newGuid()
    clonedSizeTemplate.isNewPosition = true
    let sizes = newSizeSystems[index].sizes
    const newSizes = [...sizes]
    newSizes.unshift(clonedSizeTemplate)
    newSizeSystems[index].sizes = newSizes
    setSizeSystems(newSizeSystems)
  }
  const changeSizeSystemByKey = (systemId, key, value) => {
    let index = sizeSystems.findIndex(item => item.id === systemId)
    if (index >= 0) {
      const newSizeSystems = [...sizeSystems]
      switch (key) {
        default:
          newSizeSystems[index][key] = value
          break
      }
      setSizeSystems(newSizeSystems)
    }
  }
  const changeSizeByKey = (systemId, sizeId, sizeObj) => {
    let systemIX = sizeSystems.findIndex(item => item.id === systemId)
    if (systemIX >= 0) {
      const newSizeSystems = [...sizeSystems]
      const newSizes = [...newSizeSystems[systemIX].sizes]
      let sizeIX = newSizes.findIndex(item => item.id === sizeId)
      if (sizeIX >= 0) {
        newSizes[sizeIX] = sizeObj
      }
      newSizeSystems[systemIX].sizes = newSizes
      setSizeSystems(newSizeSystems)
    }
  }
  const removeSize = (systemId, sizeId) => {
    let systemIX = sizeSystems.findIndex(item => item.id === systemId)
    if (systemIX >= 0) {
      const newSizeSystems = [...sizeSystems]
      const newSizes = [...newSizeSystems[systemIX].sizes]
      let sizeIX = newSizes.findIndex(item => item.id === sizeId)
      if (sizeIX >= 0) {
        newSizes.splice(sizeIX, 1)
      }
      newSizeSystems[systemIX].sizes = newSizes
      setSizeSystems(newSizeSystems)
    }
  }
  const resetErrorByFormKey = useCallback(formKey => {
    setFormErrors(prev => {
      if (prev[formKey]) {
        delete prev[formKey]
      }
      return prev
    })
  }, [setFormErrors])
  const resetErrorStatesByFormKey = useCallback((form, key) => {
    setErrorStates(prev => {
      if (prev[form]?.formErrors) {
        delete prev[form].formErrors[key]
      }
      return prev
    })
  }, [setErrorStates])
  const getFormErrorByKey = (form, key) => {
    switch (form) {
      case "saveSizeSystems":
        return formErrors[`sizeData${key}`] || errorStates[form]?.formErrors[`data${key}`]
      case "saveArticle":
        return formErrors[`baseData${key}`] || errorStates[form]?.formErrors[key]
      case "saveSize":
        return formErrors[key] || errorStates[form]?.formErrors[key]
      default:
        return null
    }
  }
  const resetFormErrorByKey = (form, key) => {
    resetErrorByFormKey(`baseData${key}`)
    resetErrorByFormKey(`sizeData${key}`)
    resetErrorStatesByFormKey(form, key)
  }
  const resetStates = () => {
    setErrorStates({})
    setLoadingStates({})
    setSuccessStates({})
  }

  const initArticleBaseObj = useCallback(() => {
    setArticleBaseObj(JSON.parse(initialArticleBaseObj))
    resetStates()
  }, [initialArticleBaseObj])

  const initArticleOfficeAvailabilities = useCallback(() => {
    resetStates()
  }, [])



  const resetArticlesPage = useCallback(() => {
    resetStates()
    setArticle(cloneDeep(articleTemplateObj))
    setArticleBaseObj(cloneDeep(articleBaseObjTemplate))
    setInitialArticleBaseObj(JSON.stringify(articleBaseObjTemplate))
    setRedirectId('')
  }, [])

  const contextValues = {
    ///Sizesystemoverview
    putSizeSystemsOrderSequence, putSizesOrderSequence,
    getSizeSystemTemplates, getSizes, deleteSize, patchSize, postSize,
    bigData, setBigData,
    ///////
    errorStates,
    loadingStates,
    successStates, setSuccessStates,
    redirectId,
    changeSizeSystemByKey, changeSizeByKey, removeSize, addNewSizeToSystem,
    uploadDocument, removeDocument, updateDocumentDescription,
    loadArticleData, loadedArticleData,
    submitCopyArticle,
    formErrors, setFormErrors,
    resetErrorByFormKey, resetErrorStatesByFormKey, getFormErrorByKey, resetFormErrorByKey,
    errorsOccurredOnSubmit,
    setSuccessSubmit, successSubmit,
    formDataHasChanged,
    resetFormData,
    resetArticlesPage,
    article, setArticle,
    getArticleBaseData,
    patchArticleBaseData,
    resetStates,
    initArticleBaseObj,
    articleBaseObj, setArticleBaseObj,
    saveArticleOfficeAvailabilities,
    actionMode, setActionMode,
    isLoading, setIsLoading,
    suppliers, getSuppliers,
    storehouses, getStorehouses,
    units, getUnits, getSupplierAlias,
    sizeSystems, setSizeSystems, getSizeSystems, saveSizeSystems,
    documentContainers, setDocumentContainers, getDocumentContainers,
    articleDetails, setArticleDetails, getArticleDetails,
    initArticleOfficeAvailabilities,
    selectedOfficeAvailabilities, setSelectedOfficeAvailabilities, getArticleOfficeAvailabilities, selectedOfficeAvailabilitiesHasChanged
  }

  return (
    <ArticleDataContext.Provider value={contextValues}>
      {children}
    </ArticleDataContext.Provider>
  )
}

export default ArticleDataProvider